import React, { useState, useEffect } from "react";
import { Button, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../ui";
import { RefreshCcw } from "lucide-react";
import { usePenpal } from "../../../hooks/usePenpal";
import { usePublishGuide } from "../../../hooks/usePublishGuide";
import type { Guide, UserLocalGuide } from "../../../types/guide";
import { useClientAppSupportsCloudV2 } from "@/store/userStore";

interface ResyncGuideButtonProps {
  onPublishClick?: (event?: React.MouseEvent | Record<string, unknown>) => void;
  isPublishing?: boolean;
  guideToSync: Guide;
}

const ResyncGuideButton: React.FC<ResyncGuideButtonProps> = ({
  onPublishClick,
  guideToSync,
  isPublishing = false,
}) => {
  const { getAllLocalGuides, isConnected, isInIframe } = usePenpal();
  const isV2 = useClientAppSupportsCloudV2();
  const { handlePublish } = usePublishGuide({
    onError: (message) => {
      console.error("Publishing error:", message);
    },
  });

  const [isGuideLocal, setIsGuideLocal] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  // Check if we're in browser (not connected to parent app)
  const isInBrowser = !isConnected && !isInIframe;

  // Check if current guide exists locally
  useEffect(() => {
    const checkLocalGuide = async () => {
      // If in browser, skip checking
      if (isInBrowser) {
        setIsGuideLocal(false);
        return;
      }

      if(!isV2) {
        setIsGuideLocal(true);
        return;
      }

      setIsChecking(true);
      try {
        const localGuides = await getAllLocalGuides() || [];

        const guideExists = localGuides.some(
          (localGuide: UserLocalGuide) => localGuide.id == guideToSync.id || localGuide.id == guideToSync.internalId
        );
        setIsGuideLocal(guideExists);
      } catch (error) {
        console.error("Error checking local guides:", error);
        setIsGuideLocal(false);
      } finally {
        setIsChecking(false);
      }
    };

    checkLocalGuide();
  }, [guideToSync, getAllLocalGuides, isV2, isInBrowser]);

  const handleClick = (event: React.MouseEvent) => {
    if (onPublishClick) {
      onPublishClick(event);
    } else {
      handlePublish({}, guideToSync || undefined);
    }
  };

  const isDisabled = isPublishing || isChecking || isGuideLocal === false || isInBrowser;

  const button = (
    <Button
      size="sm"
      onClick={handleClick}
      disabled={isDisabled}
    >
      <RefreshCcw size={16} className="mr-2" />
      Re-sync guide
    </Button>
  );

  // If in browser, show tooltip that it should be opened from the app
  if (isInBrowser) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {button}
          </TooltipTrigger>
          <TooltipContent>
            <p>Guide resyncing is only available when opened from the app</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // If guide is not local, show tooltip
  if (isGuideLocal === false) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {button}
          </TooltipTrigger>
          <TooltipContent>
            <p>Guide is not on user's machine and can't be synced</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return button;
};

export default ResyncGuideButton;
